import React, { useState, useCallback } from 'react';
import { useWallet } from '@/contexts/WalletContext';
import Button from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';
import DepositModal from '@/components/deposit/DepositModal';
import { AnimatePresence, motion } from 'framer-motion';
import styles from '@/styles/components/wallet/BalanceSection.module.css';

interface BalanceSectionProps {
  onTopUpClick?: () => void;
}

const BalanceSection: React.FC<BalanceSectionProps> = ({ onTopUpClick }) => {
  const { state, isLoading } = useWallet();
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);

  const handleTopUpClick = useCallback(() => {
    if (onTopUpClick) {
      onTopUpClick();
    } else {
      setIsDepositModalOpen(true);
    }
  }, [onTopUpClick]);

  const handleDepositClose = useCallback(() => {
    setIsDepositModalOpen(false);
  }, []);

  const formatSWM = (amount: string | undefined) => {
    const num = parseFloat(amount || '0');
    if (Number.isNaN(num)) return '0.00';
    return num.toFixed(2);
  };

  const hasTrialBalance = state.trialBalance > 0;
  const trialDisplay = `${Math.round(state.trialBalance)} trial`;

  return (
    <>
      <div className={styles.setting}>
        <div className={styles.details}>
          <p className={styles.totalBalance}>AVAILABLE BALANCE</p>
          <div className={styles.header}>
            <h2 className={styles.usd}>
              {isLoading ? (
                <Spinner size="md" color="primary" />
              ) : (
                `${formatSWM(state.swmBalance.pool)} USD`
              )}
            </h2>
          </div>
          <AnimatePresence>
            {hasTrialBalance && (
              <motion.div
                className={styles.trialBalance}
                initial={{ scale: 0.85, opacity: 0, y: -4 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 280, damping: 22 }}
              >
                <span className={styles.trialLabel}>Trial balance</span>
                <span className={styles.trialValue}>{trialDisplay}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className={styles.actions}>
          <Button
            variant="secondary"
            size="md"
            onClick={handleTopUpClick}
            disabled={isLoading}
            className={styles.topUpButton}
          >
            Top up balance
          </Button>
        </div>
      </div>

      <DepositModal
        isOpen={isDepositModalOpen}
        onClose={handleDepositClose}
        userAddress={state.address || undefined}
      />
    </>
  );
};

export default BalanceSection;
